/**
 * SYSTEM SETTINGS MODULE — Product SKUs.
 * Ported from the source app's ProductSKUsSection. A SKU is assembled from a
 * Flavor + Bottle Size + Pack Size (+ optional tags); sizes/flavors/pack sizes
 * are entered inline via CreatableSelect (pick or type-to-create), tags via a
 * creatable multi-select, and the Product SKU name is generated automatically
 * as "flavor / size / pack" (buildSkuName).
 *
 * sales-forecasting reads these (name, size, flavor, pack, tag, active). Data
 * access is the shared referenceDataService (admin module -> shared).
 */

import { useEffect, useState } from "react";
import CreatableSelect from "../../../shared/components/CreatableSelect.jsx";
import CreatableMultiSelect from "../../../shared/components/CreatableMultiSelect.jsx";
import ProductSpecModal from "../../../shared/components/ProductSpecModal.jsx";
import {
  listProducts, createProduct, updateProduct, deleteProduct,
  listSizes, createSize,
  listFlavors, createFlavor,
  listPackSizes, createPackSize,
} from "../../../shared/utils/referenceDataService.js";

// Product SKU name from its parts — matches the source app's buildSkuName.
function buildSkuName(flavorName, sizeName, packName) {
  return [flavorName, sizeName, packName].filter(Boolean).join(" / ");
}

const byName = (a, b) => a.name.localeCompare(b.name);
const EMPTY = { flavorId: "", sizeId: "", packId: "", tags: [] };

export default function ProductSkusSection() {
  const [products, setProducts] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [flavors, setFlavors] = useState([]);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [add, setAdd] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState(EMPTY);
  const [specProduct, setSpecProduct] = useState(null); // product whose full spec ("More") is open

  async function load() {
    setErr(null);
    try {
      const [p, s, f, k] = await Promise.all([listProducts(), listSizes(), listFlavors(), listPackSizes()]);
      setProducts(p); setSizes(s); setFlavors(f); setPacks(k);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function run(fn) {
    setBusy(true);
    setErr(null);
    try { await fn(); await load(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  // Inline create from a CreatableSelect — append to the local list, return the row.
  const onCreateFlavor = async (name) => { const it = await createFlavor({ name }); setFlavors((p) => [...p, it].sort(byName)); return it; };
  const onCreateSize   = async (name) => { const it = await createSize({ name });   setSizes((p) => [...p, it].sort(byName));   return it; };
  const onCreatePack   = async (name) => { const it = await createPackSize({ name }); setPacks((p) => [...p, it].sort(byName));  return it; };

  const nameOf = (list, id) => list.find((x) => String(x.id) === String(id))?.name ?? "";
  const addName = buildSkuName(nameOf(flavors, add.flavorId), nameOf(sizes, add.sizeId), nameOf(packs, add.packId));
  const editName = buildSkuName(nameOf(flavors, editRow.flavorId), nameOf(sizes, editRow.sizeId), nameOf(packs, editRow.packId));

  // Tags already used across this account's SKUs — pick-from list for the tag control.
  const allTags = [...new Set(products.flatMap((p) => p.tag || []))].sort((a, b) => a.localeCompare(b));

  async function handleAdd() {
    if (!add.flavorId) { setErr("Flavor is required."); return; }
    if (!add.sizeId) { setErr("Bottle size is required."); return; }
    await run(async () => {
      await createProduct({ name: addName, flavorId: add.flavorId, sizeId: add.sizeId, packSizeId: add.packId, tag: add.tags });
      setAdd(EMPTY);
    });
  }

  function startEdit(p) {
    setEditId(p.id);
    setEditRow({ flavorId: p.flavor_id || "", sizeId: p.size_id || "", packId: p.pack_size_id || "", tags: p.tag || [] });
  }
  async function saveEdit(id) {
    if (!editRow.flavorId || !editRow.sizeId) { setErr("Flavor and bottle size are required."); return; }
    await run(async () => {
      await updateProduct(id, { name: editName, flavorId: editRow.flavorId, sizeId: editRow.sizeId, packSizeId: editRow.packId, tag: editRow.tags });
      setEditId(null);
    });
  }

  const cell = { padding: "9px 10px", borderBottom: "1px solid var(--border)", verticalAlign: "top" };
  const field = { flex: "1 1 140px", margin: 0 };
  const skuBox = {
    height: 38, boxSizing: "border-box", display: "flex", alignItems: "center", padding: "0 10px",
    border: "1px solid var(--border)", borderRadius: "var(--r-control)",
    background: "var(--sunken)", fontSize: 14, fontWeight: 600, lineHeight: 1.3,
  };
  // While editing, drop the overflow clip so in-cell create dropdowns (esp. on
  // the last row) aren't cut off; keep horizontal scroll when not editing.
  const tableWrapStyle = editId ? undefined : { overflowX: "auto" };

  return (
    <section className="settings-card wide">
      <h2 style={{ marginTop: 0 }}>Product SKUs</h2>
      <p className="muted">
        Build a SKU from a flavor, bottle size, and pack size — type a new one in
        any box to create it on the fly. The Product SKU name is generated
        automatically. The Sales Forecast (and other modules) read these SKUs.
      </p>

      {err && <p className="error">{err}</p>}

      {/* Add SKU — Product SKU (generated) is the leftmost field, built from the rest */}
      <div style={{ display: "flex", flexDirection: "row", gap: 12, flexWrap: "wrap", alignItems: "flex-end", margin: "4px 0 18px" }}>
        <label style={{ flex: "1.4 1 180px", margin: 0 }}>
          Product SKU
          <div style={skuBox}>
            {addName || <span className="muted" style={{ fontWeight: 400 }}>pick flavor, size &amp; pack</span>}
          </div>
        </label>
        <label style={field}>
          Flavor
          <CreatableSelect options={flavors} value={add.flavorId} onChange={(id) => setAdd({ ...add, flavorId: id })} onCreate={onCreateFlavor} placeholder="Select or type flavor…" />
        </label>
        <label style={field}>
          Bottle Size
          <CreatableSelect options={sizes} value={add.sizeId} onChange={(id) => setAdd({ ...add, sizeId: id })} onCreate={onCreateSize} placeholder="Select or type size…" />
        </label>
        <label style={field}>
          Pack Size
          <CreatableSelect options={packs} value={add.packId} onChange={(id) => setAdd({ ...add, packId: id })} onCreate={onCreatePack} placeholder="Select or type pack…" />
        </label>
        <label style={field}>
          Tags
          <CreatableMultiSelect options={allTags} value={add.tags} onChange={(tags) => setAdd({ ...add, tags })} placeholder="Select or type tag…" />
        </label>
        <button className="btn" type="button" disabled={busy || !add.flavorId || !add.sizeId} onClick={handleAdd} style={{ flex: "0 0 auto", alignSelf: "flex-end" }}>Add SKU</button>
      </div>

      {loading ? (
        <p className="muted">Loading…</p>
      ) : products.length === 0 ? (
        <p className="muted">No SKUs yet — build your first one above.</p>
      ) : (
        <div style={tableWrapStyle}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: "left" }}>
                <th style={cell}>Product SKU</th>
                <th style={cell}>Flavor</th>
                <th style={cell}>Size</th>
                <th style={cell}>Pack</th>
                <th style={cell}>Tags</th>
                <th style={{ ...cell, textAlign: "center" }}>Active</th>
                <th style={{ ...cell, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => {
                const editing = editId === p.id;
                return (
                  <tr key={p.id}>
                    <td style={cell}>{editing ? <span className="muted">{editName || "—"}</span> : <strong>{p.name}</strong>}</td>
                    <td style={cell}>{editing ? <CreatableSelect options={flavors} value={editRow.flavorId} onChange={(id) => setEditRow({ ...editRow, flavorId: id })} onCreate={onCreateFlavor} /> : (p.flavor?.name || <span className="muted">—</span>)}</td>
                    <td style={cell}>{editing ? <CreatableSelect options={sizes} value={editRow.sizeId} onChange={(id) => setEditRow({ ...editRow, sizeId: id })} onCreate={onCreateSize} /> : (p.size?.name || <span className="muted">—</span>)}</td>
                    <td style={cell}>{editing ? <CreatableSelect options={packs} value={editRow.packId} onChange={(id) => setEditRow({ ...editRow, packId: id })} onCreate={onCreatePack} /> : (p.pack_size?.name || <span className="muted">—</span>)}</td>
                    <td style={cell}>
                      {editing
                        ? <CreatableMultiSelect options={allTags} value={editRow.tags} onChange={(tags) => setEditRow({ ...editRow, tags })} placeholder="Select or type tag…" />
                        : ((p.tag || []).length ? p.tag.join(", ") : <span className="muted">—</span>)}
                    </td>
                    <td style={{ ...cell, textAlign: "center" }}>
                      <input type="checkbox" checked={p.active} disabled={busy} onChange={(e) => run(() => updateProduct(p.id, { active: e.target.checked }))} title={p.active ? "Active" : "Inactive"} />
                    </td>
                    <td style={{ ...cell, textAlign: "right", whiteSpace: "nowrap" }}>
                      {editing ? (
                        <>
                          <button className="btn" type="button" disabled={busy} onClick={() => saveEdit(p.id)} style={{ marginRight: 8 }}>Save</button>
                          <button type="button" className="secondary" disabled={busy} onClick={() => setEditId(null)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button type="button" className="link" disabled={busy} onClick={() => setSpecProduct(p)} style={{ marginRight: 12 }} title="Edit the full product specification">More</button>
                          <button type="button" className="link" disabled={busy} onClick={() => startEdit(p)} style={{ marginRight: 12 }}>Edit</button>
                          <button type="button" className="link" style={{ color: "var(--error)" }} disabled={busy} onClick={() => { if (window.confirm(`Delete ${p.name}?`)) run(() => deleteProduct(p.id)); }}>Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {specProduct && (
        <ProductSpecModal
          product={specProduct}
          onClose={() => setSpecProduct(null)}
          onSaved={load}
        />
      )}
    </section>
  );
}
